import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEntityDetails } from '../../api/endpoints/useEntityDetails';
import {
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  Typography,
  IconButton,
} from '@locus-taxy/encore-mui';
import { WorkflowActionModal } from '@locus-taxy/encore-grid';
import { getNestedValue } from '../../utils/objectUtils';
import { useClientId } from '../../hooks/useClientId';
import DetailsSchemaModal from '../DetailsSchemaModal';
import { useWorkflowActions } from '../../hooks/useWorkflowActions';
import { useState, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import EntityDetailsBody from './EntityDetailsBody';
import type { FormSchema } from './types';
import {
  useEntityMetadata,
  type EntityMetadataResponse,
} from '../../api/endpoints/useEntityMetadata';
import { Container } from '@locus-taxy/encore-mui';
import { useModules, type ModulesConfig } from '../../api/endpoints/useModules';
import {
  buildBreadcrumbsFromModules,
  buildModulesNavigation,
} from '../../utils/modulesNavigation';
import { usePreserveSearchParams } from '../../hooks/usePreserveSearchParams';
import { buildClientPrefixedPath } from '../../utils/url';
import { PageHeader } from '@locus-taxy/encore-ui-toolkit';
import { RoutePath } from '../../types/categories';
import {
  KeyboardDoubleArrowRight,
  History as TimelineIcon,
  Cached,
} from '@locus-taxy/encore-mui/icons';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../../config';
import { DateTimeUtils, getTestId } from '@locus-taxy/encore-utils';
import MoreActionsMenu from './MoreActionsMenu';
import EntityStatusChips from './EntityStatusChips';
import EntityTeamChips from './EntityTeamChips';
import { useRouteContext } from '../../hooks/useRouteContext';
import { TestIdAction } from '../../utils/testIdUtils';
import { useTeams } from '../../api/endpoints/useTeams';
import { useEntityFormAdditionalContext } from '../../hooks/useEntityFormAdditionalContext';
import { isMfeAppConfig } from '../../utils/customApp';
import { CustomAppActionModal } from '../CustomAppActionModal';

type EntityDetailsDataPayload = {
  data?: {
    metadata?: { rootInstanceKey?: string };
    entityData?: { rootMeta?: { rootInstanceKey?: string } };
  };
};

interface EntityDetailsProps {
  entityName?: string;
}

type EntitySchemaInfo = {
  title?: string;
  timelineConfig?: { enabled?: boolean };
  entityPresentationMetadata: {
    additionalColumnConfig: {
      statusFieldPath?: string;
      subStatusFieldPath?: string;
    };
  };
};

type FlatAction = {
  id: string;
  label: string;
  icon: ReactNode;
  variant: 'outline';
  onClick: () => void;
};

const TOUR_FIELDS = [
  'id',
  'name',
  'dropOrders',
  'assignmentDetails',
  'date',
  'dropOrderIds',
  'pickupOrderIds',
  'endVisit',
  'plannedSummary',
  'startVisit',
  'timeslot',
  'tourSource',
  'tourStatus',
];
const HIDE_TIMELINE_TEXT = 'Hide timeline';
const SHOW_TIMELINE_TEXT = 'Show timeline';
const TIMELINE_UNAVAILABLE_TEXT = 'Timeline unavailable';
const REFRESH_TEXT = 'Refresh';

function resolveApiBaseUrl(clientId: string) {
  return clientId ? getApiBaseUrl(clientId) : '';
}

function resolveEntityName(
  overrideName: string | undefined,
  paramName: string | undefined
) {
  return overrideName ?? paramName ?? '';
}

function resolveTourFields(entityName: string) {
  return entityName === 'b2cDeliveryTour' ? TOUR_FIELDS : undefined;
}

function resolveEntityData(
  entityDetails: ReturnType<typeof useEntityDetails>['data']
) {
  const baseEntityData = entityDetails?.data?.data?.entityData ?? {};
  const timelineAggregations = entityDetails?.data?.data?._timelineAggregations;
  return timelineAggregations
    ? { ...baseEntityData, _timelineAggregations: timelineAggregations }
    : baseEntityData;
}

function resolveEntitySchemaInfo(
  entityMetadata: EntityMetadataResponse | null | undefined
) {
  return entityMetadata?.data?.entitySchema as EntitySchemaInfo | undefined;
}

function resolveMetadataPresentation(
  entityMetadata: EntityMetadataResponse | null | undefined
) {
  const detailsForm = entityMetadata?.data?.frontend?.detailsView?.form?.schema;
  const timelineVisible =
    entityMetadata?.data?.frontend?.detailsView?.timeline?.visible ?? true;
  const entitySchemaInfo = resolveEntitySchemaInfo(entityMetadata);
  const entityTitle = entitySchemaInfo?.title ?? '';
  const isTimelineEnabled = Boolean(entitySchemaInfo?.timelineConfig?.enabled);
  const statusFieldPath =
    entitySchemaInfo?.entityPresentationMetadata?.additionalColumnConfig
      ?.statusFieldPath;
  const subStatusFieldPath =
    entitySchemaInfo?.entityPresentationMetadata?.additionalColumnConfig
      ?.subStatusFieldPath;

  return {
    detailsForm,
    timelineVisible,
    entitySchemaInfo,
    entityTitle,
    isTimelineEnabled,
    statusFieldPath,
    subStatusFieldPath,
  };
}

function resolveActions(
  entityDetails: ReturnType<typeof useEntityDetails>['data']
) {
  return entityDetails?.data?.actions ?? [];
}

function resolveCombinedState(
  isInstanceLoading: boolean,
  isMetadataLoading: boolean,
  instanceError: unknown,
  metadataError: unknown
) {
  return {
    isLoading: isInstanceLoading || isMetadataLoading,
    error: instanceError || metadataError,
  };
}

function resolveShowOutboundButton(
  resolvedEntityName: string,
  entityData: Record<string, unknown>
) {
  return (
    resolvedEntityName === 'b2cDeliveryTour' &&
    entityData.tourStatus === 'STAGED'
  );
}

function resolveTimelineAvailability(
  rootInstanceKey: string | undefined,
  isTimelineEnabled: boolean,
  timelineVisible: boolean
) {
  return Boolean(rootInstanceKey) && isTimelineEnabled && timelineVisible;
}

function resolveHeaderTitle(
  entityData: Record<string, unknown>,
  entityId: string | undefined
) {
  return (entityData?.name as string | undefined) ?? entityId ?? '';
}

function resolveRootInstanceKey(
  entityDetailsData: unknown
): string | undefined {
  const detailsData = entityDetailsData as EntityDetailsDataPayload | undefined;
  const metadataRoot = detailsData?.data?.metadata?.rootInstanceKey;
  const entityRoot = detailsData?.data?.entityData?.rootMeta?.rootInstanceKey;
  return metadataRoot ?? entityRoot;
}

function resolveHeaderBreadcrumbs(
  moduleCrumbs: Array<{ title: string }>,
  resolvedEntityName: string
) {
  if (moduleCrumbs.length === 0) {
    return [{ id: 'fallback', label: resolvedEntityName || '' }];
  }
  return moduleCrumbs.map((crumb, i) => ({
    id: `bc-${i}`,
    label: crumb.title,
  }));
}

function resolveTimelineTooltip(
  timelineAvailable: boolean,
  isTimelineOpen: boolean,
  t: ((key: string, options?: { defaultValue: string }) => string) | undefined
) {
  const hideTimeline = t
    ? t('entityDetails.hideTimeline', { defaultValue: HIDE_TIMELINE_TEXT })
    : HIDE_TIMELINE_TEXT;
  const showTimeline = t
    ? t('entityDetails.showTimeline', { defaultValue: SHOW_TIMELINE_TEXT })
    : SHOW_TIMELINE_TEXT;
  const timelineUnavailable = t
    ? t('entityDetails.timelineUnavailable', {
        defaultValue: TIMELINE_UNAVAILABLE_TEXT,
      })
    : TIMELINE_UNAVAILABLE_TEXT;

  if (!timelineAvailable) {
    return timelineUnavailable;
  }
  return isTimelineOpen ? hideTimeline : showTimeline;
}

function resolveValidatedStatus(
  entityData: Record<string, unknown>,
  path: string | undefined,
  fallback: unknown
): string | undefined {
  if (path) {
    const pathValue = getNestedValue<string>(entityData, path);
    if (typeof pathValue === 'string' && pathValue !== '') {
      return pathValue;
    }
  }
  if (typeof fallback === 'string' && fallback !== '') {
    return fallback;
  }
  return undefined;
}

function buildFlatActions({
  isMapView,
  isNetworkMapView,
  showOutboundButton,
  openMapDetailsTooltip,
  networkMapDetailsTooltip,
  outboundTooltip,
  entityId,
  clientId,
  resolvedEntityName,
  navigate,
}: {
  isMapView: boolean;
  isNetworkMapView: boolean;
  showOutboundButton: boolean;
  openMapDetailsTooltip: string;
  networkMapDetailsTooltip: string;
  outboundTooltip: string;
  entityId: string | undefined;
  clientId: string;
  resolvedEntityName: string;
  navigate: ReturnType<typeof useNavigate>;
}): FlatAction[] {
  const actions: FlatAction[] = [];
  if (isMapView) {
    actions.push({
      id: 'open-map-details',
      label: openMapDetailsTooltip,
      icon: (
        <Tooltip title={openMapDetailsTooltip}>
          <KeyboardDoubleArrowRight />
        </Tooltip>
      ),
      variant: 'outline',
      onClick: () => {
        const url = buildClientPrefixedPath(
          `${RoutePath.MAP_DETAILS}/${entityId}`,
          clientId
        );
        navigate(url);
      },
    });
  }
  if (isNetworkMapView) {
    actions.push({
      id: 'open-network-map-details',
      label: networkMapDetailsTooltip,
      icon: (
        <Tooltip title={networkMapDetailsTooltip}>
          <KeyboardDoubleArrowRight />
        </Tooltip>
      ),
      variant: 'outline',
      onClick: () => {
        const url = buildClientPrefixedPath(
          `${RoutePath.NETWORK_MAP_DETAILS}/${entityId}`,
          clientId
        );
        navigate(url);
      },
    });
  }
  if (showOutboundButton) {
    actions.push({
      id: 'outbound',
      label: outboundTooltip,
      icon: (
        <Tooltip title={outboundTooltip}>
          <KeyboardDoubleArrowRight />
        </Tooltip>
      ),
      variant: 'outline',
      onClick: () => {
        const url = buildClientPrefixedPath(
          `/entities/${resolvedEntityName}/${entityId}/outbound`,
          clientId
        );
        navigate(url);
      },
    });
  }
  return actions;
}

function useTimelineOpenState({
  isMdUp,
  rootInstanceKey,
  isTimelineEnabled,
  timelineAvailable,
}: {
  isMdUp: boolean;
  rootInstanceKey: string | undefined;
  isTimelineEnabled: boolean;
  timelineAvailable: boolean;
}) {
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  useEffect(() => {
    if (!rootInstanceKey || !isTimelineEnabled) {
      setIsTimelineOpen(false);
      return;
    }
    setIsTimelineOpen(isMdUp);
  }, [isMdUp, rootInstanceKey, isTimelineEnabled]);

  const handleTimelineToggle = () => {
    if (!timelineAvailable) {
      return;
    }
    setIsTimelineOpen(prev => !prev);
  };

  return { isTimelineOpen, setIsTimelineOpen, handleTimelineToggle };
}

function useLastUpdatedDuration({
  entityDetailsData,
  isInstanceLoading,
}: {
  entityDetailsData: unknown;
  isInstanceLoading: boolean;
}) {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (entityDetailsData && !isInstanceLoading) {
      setLastUpdatedAt(Date.now());
    }
  }, [entityDetailsData, isInstanceLoading]);

  useEffect(() => {
    if (!lastUpdatedAt) {
      return;
    }
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, [lastUpdatedAt]);

  const lastUpdatedDuration = useMemo(() => {
    if (!lastUpdatedAt) {
      return null;
    }
    const dateTimeUtils = DateTimeUtils.getInstance();
    const updatedTime = dateTimeUtils.toDayjs(lastUpdatedAt);
    return dateTimeUtils.formatRelative(updatedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdatedAt, currentTime]);

  return { lastUpdatedDuration, setLastUpdatedAt };
}

function buildActionStack({
  isTimelineEnabled,
  timelineTooltip,
  isTimelineOpen,
  timelineAvailable,
  handleTimelineToggle,
}: {
  isTimelineEnabled: boolean;
  timelineTooltip: string;
  isTimelineOpen: boolean;
  timelineAvailable: boolean;
  handleTimelineToggle: () => void;
}) {
  if (!isTimelineEnabled) {
    return [];
  }
  return [
    {
      id: 'toggle-timeline',
      icon: (
        <Tooltip title={timelineTooltip}>
          <TimelineIcon
            color={isTimelineOpen && timelineAvailable ? 'primary' : 'inherit'}
          />
        </Tooltip>
      ),
      ariaLabel: timelineTooltip,
      onClick: handleTimelineToggle,
      disabled: !timelineAvailable,
    },
  ];
}

function buildSubheading({
  lastUpdatedDuration,
  isInstanceLoading,
  t,
  refreshLabel,
  handleRefresh,
  resolvedEntityName,
}: {
  lastUpdatedDuration: string | null;
  isInstanceLoading: boolean;
  t: (key: string, options?: { defaultValue: string }) => string;
  refreshLabel: string;
  handleRefresh: () => void;
  resolvedEntityName: string;
}) {
  if (!lastUpdatedDuration) {
    return undefined;
  }

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        // @ts-expect-error - body3 is not a valid variant
        variant="body3"
        color="text.secondary"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'last-updated-time',
          entity: resolvedEntityName || undefined,
          page: 'details',
        })}
      >
        {isInstanceLoading
          ? t('gridView.updating', { defaultValue: 'Updating...' })
          : `Updated ${lastUpdatedDuration}`}
      </Typography>
      <Tooltip title={refreshLabel} placement="bottom">
        <IconButton
          size="small"
          onClick={handleRefresh}
          aria-label={refreshLabel}
          disabled={isInstanceLoading}
          sx={{ width: 18, height: 18, minWidth: 18, padding: 0 }}
          data-testid={getTestId({
            action: TestIdAction.REFRESH,
            object: 'button',
            entity: resolvedEntityName || undefined,
            page: 'details',
          })}
        >
          <Cached sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function EntityDetailsContainer({
  entityName: entityNameOverride,
}: Readonly<EntityDetailsProps> = {}) {
  const { entityName: entityNameParam, entityId } = useParams();
  const clientId = useClientId();
  const apiBaseUrl = useMemo(() => resolveApiBaseUrl(clientId), [clientId]);
  const location = useLocation();
  const routeContext = useRouteContext();
  const { additionalContext: entityFormAdditionalContext } =
    useEntityFormAdditionalContext();
  const resolvedEntityName = resolveEntityName(
    entityNameOverride,
    entityNameParam
  );
  const {
    data: entityDetails,
    isLoading: isInstanceLoading,
    error: instanceError,
    refetch: refetchEntityDetails,
  } = useEntityDetails(
    clientId,
    resolvedEntityName,
    entityId ?? '',
    resolveTourFields(resolvedEntityName),
    true // Include relations to fetch timeline aggregations
  );

  const entityData = useMemo(
    () => resolveEntityData(entityDetails),
    [entityDetails]
  );

  const actions = resolveActions(entityDetails);
  const {
    data: entityMetadata,
    isLoading: isMetadataLoading,
    error: metadataError,
  } = useEntityMetadata(clientId, resolvedEntityName);

  const {
    detailsForm,
    timelineVisible,
    entitySchemaInfo,
    entityTitle,
    isTimelineEnabled,
    statusFieldPath,
    subStatusFieldPath,
  } = resolveMetadataPresentation(entityMetadata);

  // Extract custom app config from metadata for details view
  const detailsViewApp = entityMetadata?.data?.frontend?.detailsView?.app;
  // Fetch teams for team chips display
  const { data: teams = [] } = useTeams(clientId);

  // Combined loading and error states
  const { isLoading, error } = resolveCombinedState(
    isInstanceLoading,
    isMetadataLoading,
    instanceError,
    metadataError
  );

  // Schema modal state
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);

  // Use workflow actions hook for single entity actions
  const {
    currentWorkflowAction,
    isWorkflowLoading,
    modalVariant,
    handleWorkflowFormSubmit,
    handleWorkflowCloseModal,
    handleWorkflowActionClick,
  } = useWorkflowActions({
    clientId,
    entityName: resolvedEntityName,
    entityId: entityId ?? '',
    refreshData: () => {
      void refetchEntityDetails();
    },
  });

  const handleActionSubmit = (data: Record<string, unknown>) => {
    handleWorkflowFormSubmit(data);
  };

  const rootInstanceKey = useMemo(
    () => resolveRootInstanceKey(entityDetails?.data),
    [entityDetails?.data]
  );

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'));
  const timelineAvailable = resolveTimelineAvailability(
    rootInstanceKey,
    isTimelineEnabled,
    timelineVisible
  );
  const { isTimelineOpen, setIsTimelineOpen, handleTimelineToggle } =
    useTimelineOpenState({
      isMdUp,
      rootInstanceKey,
      isTimelineEnabled,
      timelineAvailable,
    });
  const { lastUpdatedDuration, setLastUpdatedAt } = useLastUpdatedDuration({
    entityDetailsData: entityDetails?.data,
    isInstanceLoading,
  });
  const [mfeRemountKey, setMfeRemountKey] = useState(0);

  // Build breadcrumbs from modules navigation
  const { data: modulesResponse } = useModules(clientId);
  const { leaves: moduleLeaves } = useMemo(() => {
    const modulesData = modulesResponse?.data as ModulesConfig | undefined;
    const rootItems = modulesData?.modules ?? [];
    return buildModulesNavigation(rootItems, clientId);
  }, [modulesResponse?.data, clientId]);

  const moduleCrumbs = useMemo(
    () => buildBreadcrumbsFromModules(location.pathname, moduleLeaves),
    [location.pathname, moduleLeaves]
  );

  // Header props
  const navigate = useNavigate();
  const { getUrlWithSearchParams } = usePreserveSearchParams();
  const { t } = useTranslation();
  const backHref = getUrlWithSearchParams(
    buildClientPrefixedPath(`/entities/${resolvedEntityName}`, clientId),
    ['teams']
  );
  const headerBreadcrumbs = useMemo(
    () => resolveHeaderBreadcrumbs(moduleCrumbs, resolvedEntityName),
    [moduleCrumbs, resolvedEntityName]
  );

  // Header actions
  const showOutboundButton = resolveShowOutboundButton(
    resolvedEntityName,
    entityData
  );
  const isMapView = resolvedEntityName === 'map';
  const isNetworkMapView = resolvedEntityName === 'networkMap';
  // Tooltip titles
  const openMapDetailsTooltip = 'Open Map Details';
  const networkMapDetailsTooltip = 'Open Network Map Details';
  const outboundTooltip = 'Outbound';

  const timelineTooltip = resolveTimelineTooltip(
    timelineAvailable,
    isTimelineOpen,
    t
  );
  const flatActions = buildFlatActions({
    isMapView,
    isNetworkMapView,
    showOutboundButton,
    openMapDetailsTooltip,
    networkMapDetailsTooltip,
    outboundTooltip,
    entityId,
    clientId,
    resolvedEntityName,
    navigate,
  });

  const dropdownActions = (actions || []).map(action => ({
    type: 'item' as const,
    id: action.id,
    label: action.label,
    onClick: () => handleWorkflowActionClick(action),
  }));

  const handleViewSchemaClick = () => {
    setSchemaModalOpen(true);
  };

  const rightSlot = (
    <MoreActionsMenu onViewSchema={handleViewSchemaClick} t={t} />
  );

  // Handle refresh
  const handleRefresh = () => {
    // Update timestamp immediately to show refresh action
    setLastUpdatedAt(Date.now());
    refetchEntityDetails();

    // Increment MFE remount key for custom apps
    setMfeRemountKey(prev => prev + 1);
  };

  const actionStack = buildActionStack({
    isTimelineEnabled,
    timelineTooltip,
    isTimelineOpen,
    timelineAvailable,
    handleTimelineToggle,
  });

  const status = resolveValidatedStatus(
    entityData,
    statusFieldPath,
    entityData?.status
  );
  const subStatus = resolveValidatedStatus(
    entityData,
    subStatusFieldPath,
    entityData?.subStatus
  );
  const teamIds = entityData?.teamIds as string[] | undefined;

  const titleAdornment = (
    <Stack direction="row" gap={1} alignItems="center">
      <EntityTeamChips teamIds={teamIds} teams={teams} />
      <EntityStatusChips status={status} subStatus={subStatus} />
    </Stack>
  );

  const workflowRouteContext = useMemo(
    () => ({
      ...routeContext,
      // Add the workflow action label or id to the entity name
      entity: `${routeContext.entity}-${currentWorkflowAction?.label ?? currentWorkflowAction?.id}`,
    }),
    [routeContext, currentWorkflowAction]
  );

  const refreshLabel = String(
    t('gridView.refresh', { defaultValue: REFRESH_TEXT })
  );
  const subheading = buildSubheading({
    lastUpdatedDuration,
    isInstanceLoading,
    t,
    refreshLabel,
    handleRefresh,
    resolvedEntityName,
  });

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme => theme.zIndex.appBar,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <PageHeader
          title={resolveHeaderTitle(entityData, entityId)}
          titleAdornment={titleAdornment}
          breadcrumbs={headerBreadcrumbs}
          onBackClick={() => navigate(backHref)}
          flatActions={flatActions}
          dropdownActions={dropdownActions}
          actionStack={actionStack}
          subheading={subheading}
          routeContext={routeContext}
          labels={{
            otherActionsLabel: t('schemaModal.actions'),
          }}
          slots={{
            right: rightSlot,
          }}
          leftPadding="4px"
        />
      </Box>
      <Container
        maxWidth="xl"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'entity-details-container',
          entity: resolvedEntityName || undefined,
          page: 'details',
        })}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <EntityDetailsBody
          clientId={clientId}
          entityName={resolvedEntityName}
          entityTitle={entityTitle}
          rootInstanceKey={rootInstanceKey}
          entityData={entityData}
          detailsForm={(detailsForm || undefined) as FormSchema | undefined}
          isLoading={isLoading}
          error={error}
          showTimeline={isTimelineOpen && timelineAvailable}
          onTimelineClose={() => setIsTimelineOpen(false)}
          mfeRemountKey={mfeRemountKey}
          entitySchemaInfo={entitySchemaInfo}
          detailsViewApp={detailsViewApp}
        />

        {/* Action Modal — MFE custom app or Camunda form */}
        {currentWorkflowAction?.customApp &&
        isMfeAppConfig(currentWorkflowAction.customApp) ? (
          <CustomAppActionModal
            open={!!currentWorkflowAction}
            onClose={handleWorkflowCloseModal}
            customApp={currentWorkflowAction.customApp}
            title={currentWorkflowAction.label}
            clientId={clientId}
            entityName={resolvedEntityName}
            entityData={entityData}
          />
        ) : (
          <WorkflowActionModal
            entityData={entityData}
            open={!!currentWorkflowAction}
            onClose={handleWorkflowCloseModal}
            form={currentWorkflowAction?.form?.schema}
            onSubmit={handleActionSubmit}
            isLoading={isWorkflowLoading}
            title={currentWorkflowAction?.label}
            entityName={resolvedEntityName}
            actionType="single"
            clientId={clientId}
            apiBaseUrl={apiBaseUrl}
            googleApiKey={GOOGLE_MAPS_API_KEY}
            routeContext={workflowRouteContext}
            additionalContext={entityFormAdditionalContext}
            {...modalVariant}
          />
        )}

        {/* Schema Modal */}
        <DetailsSchemaModal
          open={schemaModalOpen}
          onClose={() => setSchemaModalOpen(false)}
          entityMetadata={entityMetadata}
          entityName={entityTitle}
          actions={actions}
          entityData={entityDetails?.data}
        />
      </Container>
    </Stack>
  );
}

export default function EntityDetails(
  props: Readonly<EntityDetailsProps> = {}
) {
  return <EntityDetailsContainer {...props} />;
}
